import React from "react";
import { User } from "../../slices/usersSlice";
import UserManagementActionMenu from "../UserManagementActionMenu/UserManagementActionMenu";
import { Column } from "@/components/Datatable/types";
import { CheckCircle2, XCircle, Minus } from "lucide-react";

interface CreateUsersColumnsProps {
  handleEdit: (uuid: string) => void;
  handleImpersonateUser: (uuid: string) => void;
  handleViewSyncLogs: (user: User) => void;
  handleSyncUser: (user: User) => void;
  isSuperAdmin: boolean;
}

const SyncStatusIcon: React.FC<{ appResult: any }> = ({ appResult }) => {
  if (!appResult) {
    return <Minus className="h-4 w-4 text-gray-400" />;
  }

  const success = appResult.response?.success || appResult.response?.Success;

  if (success) {
    return <CheckCircle2 className="h-4 w-4 text-green-500" />;
  }

  return <XCircle className="h-4 w-4 text-red-500" />;
};

export function createUsersColumns({
  handleEdit,
  handleImpersonateUser,
  handleViewSyncLogs,
  handleSyncUser,
  isSuperAdmin,
}: CreateUsersColumnsProps): Column<User>[] {
  const baseColumns: Column<User>[] = [
    {
      header: "Employee ID",
      accessorKey: "employeeId",
    },
    {
      header: "First Name",
      accessorKey: "firstName",
      enableSorting: true,
    },
    {
      header: "Last Name",
      accessorKey: "lastName",
      enableSorting: true,
    },
    {
      header: "Email",
      accessorKey: "email",
      enableSorting: true,
    },
    {
      header: "Salesforce ID",
      accessorKey: "salesforceId",
    },
  ];

  const syncColumn: Column<User>[] = isSuperAdmin
    ? [
        {
          id: "syncStatus",
          header: "Sync Status",
          cell: ({ row }) => {
            const user = row.original;
            const syncResults = user.remoteSyncLog?.syncResults;
            return (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-1" title="MemberPortal">
                  <span className="text-xs text-gray-500">MP</span>
                  <SyncStatusIcon appResult={syncResults?.memberportal} />
                </div>
                <div className="flex items-center gap-1" title="PartnerNetwork">
                  <span className="text-xs text-gray-500">PN</span>
                  <SyncStatusIcon appResult={syncResults?.partnernetwork} />
                </div>
                <div className="flex items-center gap-1" title="Scoreboard">
                  <span className="text-xs text-gray-500">SB</span>
                  <SyncStatusIcon appResult={syncResults?.scoreboard} />
                </div>
              </div>
            );
          },
        },
      ]
    : [];

  const actionsColumn: Column<User>[] = [
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => {
        const userItem = row.original;
        return (
          <UserManagementActionMenu
            employeeUuid={userItem.employeeUuid || ""}
            onEdit={handleEdit}
            onImpersonate={handleImpersonateUser}
            onViewSyncLogs={() => handleViewSyncLogs(userItem)}
            onSyncUser={() => handleSyncUser(userItem)}
            userUuid={userItem.uuid || ""}
            isSuperAdmin={isSuperAdmin}
          />
        );
      },
    },
  ];

  return [...baseColumns, ...syncColumn, ...actionsColumn];
}
